import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axiosInstance from '../../axiosConfig';
import {
  MarketplaceFooter,
  MarketplaceHeader,
  MarketplaceSectionHeading,
  MerchantStoreCard,
  ProductCard
} from './MarketplaceShared';
import MarketplaceSeo, { marketplaceUrlFor } from './MarketplaceSeo';
import './Marketplace.css';

function MarketplaceMerchantsPage() {
  const [searchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || '';
  const [payload, setPayload] = useState({ merchants: [], products: [], total: 0, totalProducts: 0, page: 1, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setSearchQuery(searchParams.get('search') || '');
    setPage(1);
  }, [searchParams]);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await axiosInstance.get('/marketplace/merchants', {
          params: {
            page,
            limit: 12,
            ...(searchQuery.trim() ? { search: searchQuery.trim() } : {})
          }
        });
        if (!data?.success) {
          throw new Error(data?.message || 'Failed to load merchants');
        }
        if (alive) {
          setPayload({
            merchants: data.merchants || [],
            products: data.products || [],
            total: data.total || 0,
            totalProducts: data.totalProducts || 0,
            page: data.page || 1,
            totalPages: data.totalPages || 1
          });
        }
      } catch (err) {
        if (alive) setError(err?.response?.data?.message || err.message || 'Failed to load merchants');
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [page, searchQuery]);

  const stats = useMemo(() => {
    const merchants = payload.merchants || [];
    return {
      domains: merchants.filter((merchant) => merchant.customDomain).length,
      products: merchants.reduce((sum, merchant) => sum + Number(merchant.productCount || 0), 0)
    };
  }, [payload]);

  const seoTitle = searchQuery
    ? `Search: ${searchQuery}`
    : 'Browse live Revstar stores';
  const seoDescription = searchQuery
    ? `Marketplace search results for "${searchQuery}" — ${payload.totalProducts} products across ${payload.total} live stores.`
    : `Explore ${payload.total || ''} live Revstar merchant storefronts and shop directly from the merchants you love.`;
  const listJsonLd = payload.merchants && payload.merchants.length ? {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: payload.merchants.slice(0, 20).map((merchant, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: marketplaceUrlFor(`/merchant/${merchant.slug}`),
      name: merchant.name
    }))
  } : null;

  const initialLoading = loading && !payload.merchants.length && !payload.products.length;

  if (initialLoading) {
    return (
      <div className="marketplace-page">
        <MarketplaceHeader active="merchants" />
        <div className="marketplace-shell marketplace-shell--centered">
          <section className="marketplace-feedback-card marketplace-loading-card">
            <h3>Loading stores</h3>
            <p>Pulling live Revstar merchant storefronts.</p>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="marketplace-page">
      <MarketplaceSeo
        title={seoTitle}
        description={seoDescription}
        path={searchQuery ? `/merchants?search=${encodeURIComponent(searchQuery)}` : '/merchants'}
        jsonLd={listJsonLd}
      />
      <MarketplaceHeader active="merchants" />

      <div className="marketplace-shell marketplace-shell--spacious">
        <section className="marketplace-section">
          <MarketplaceSectionHeading
            eyebrow={searchQuery ? 'Search results' : 'All stores'}
            title={searchQuery ? `Results for "${searchQuery}"` : 'Merchant storefronts'}
            description={loading
              ? 'Loading marketplace matches.'
              : searchQuery
                ? `${payload.totalProducts} products and ${payload.total} stores matched your search.`
                : `${payload.total} stores available. ${stats.products} products visible across this page.`}
          />

          {error ? (
            <div className="marketplace-feedback-card">
              <h3>Merchant directory unavailable</h3>
              <p>{error}</p>
            </div>
          ) : null}

          {!error && !loading && payload.merchants.length === 0 && payload.products.length === 0 ? (
            <div className="marketplace-feedback-card">
              <h3>No results found</h3>
              <p>Try another keyword or clear the search to see more products and storefronts.</p>
            </div>
          ) : null}

          {!error && searchQuery && payload.products.length > 0 ? (
            <section className="marketplace-search-block">
              <MarketplaceSectionHeading
                eyebrow="Matching products"
                title="Products from Revstar stores"
                description={`${payload.totalProducts} product${payload.totalProducts === 1 ? '' : 's'} matched "${searchQuery}".`}
              />
              <div className="marketplace-product-grid marketplace-product-grid--dense">
                {payload.products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            </section>
          ) : null}

          {!error && payload.merchants.length > 0 ? (
            <section className="marketplace-search-block">
              {searchQuery ? (
                <MarketplaceSectionHeading
                  eyebrow="Matching stores"
                  title="Storefronts"
                  description={`${payload.total} store${payload.total === 1 ? '' : 's'} matched "${searchQuery}".`}
                />
              ) : null}
              <div className="marketplace-merchant-grid">
                {payload.merchants.map((merchant) => (
                  <MerchantStoreCard key={merchant.slug} merchant={merchant} />
                ))}
              </div>

              <div className="marketplace-pagination">
                <button
                  type="button"
                  className="marketplace-page-btn"
                  disabled={payload.page <= 1 || loading}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                >
                  Previous
                </button>
                <span>Page {payload.page} of {payload.totalPages}</span>
                <button
                  type="button"
                  className="marketplace-page-btn"
                  disabled={payload.page >= payload.totalPages || loading}
                  onClick={() => setPage((current) => Math.min(payload.totalPages, current + 1))}
                >
                  Next
                </button>
              </div>
            </section>
          ) : null}
        </section>
      </div>
      <MarketplaceFooter />
    </div>
  );
}

export default MarketplaceMerchantsPage;
